import { Flex, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconCircleFilled } from '@tabler/icons-react';
import { useState } from 'react';
import DatasetPill from '@/pages/Run/components/DatasetPill.tsx';
import { StepRunDataflowInput, StepRunDataflowOutput } from '@/types/stepExecutions.ts';

const MAX_VISIBLE_BINDINGS = 3;

type DataflowBinding = StepRunDataflowInput | StepRunDataflowOutput;

type StepRunDataflowSectionProps<T extends DataflowBinding> = {
  title: string;
  bindings: T[];
  getRoleLabel: (binding: T) => string | null;
};

const StepRunDataflowSection = <T extends DataflowBinding>({
  title,
  bindings,
  getRoleLabel,
}: StepRunDataflowSectionProps<T>) => {
  const isCollapsible = bindings.length > MAX_VISIBLE_BINDINGS;
  const [isExpanded, setIsExpanded] = useState(!isCollapsible);
  const visibleBindings = isExpanded ? bindings : bindings.slice(0, MAX_VISIBLE_BINDINGS);
  const hiddenBindingCount = bindings.length - MAX_VISIBLE_BINDINGS;
  const noun = title.toLowerCase();
  const moreLabel = `Show ${hiddenBindingCount} more ${noun.slice(0, -1)}${hiddenBindingCount === 1 ? '' : 's'}`;
  const toggleLabel = isExpanded ? `Show fewer ${noun}` : moreLabel;

  if (bindings.length === 0) {
    return null;
  }

  return (
    <Stack gap={5} pos="relative">
      <Group gap={6} wrap="nowrap">
        <Text size="11px" fw={600} c="dimmed" tt="uppercase">
          {title}
        </Text>
      </Group>
      <div
        style={{
          position: 'absolute',
          left: '9px',
          top: '28px',
          bottom: isCollapsible ? '28px' : '5px',
          width: '1px',
          backgroundColor: '#EAEAEA',
          zIndex: 0,
        }}
      />
      <Stack gap={5}>
        {visibleBindings.map((binding, index) => {
          const roleLabel = getRoleLabel(binding);
          return (
            <Group
              key={`${binding.dataset.id}-${index}`}
              gap="xs"
              wrap="nowrap"
              align="flex-start"
              style={{ maxWidth: '100%', minWidth: 0 }}
            >
              <Flex
                mt="1px"
                w={18}
                h={18}
                align="center"
                justify="center"
                style={{
                  position: 'relative',
                  zIndex: 1,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  flexShrink: 0,
                }}
              >
                <IconCircleFilled size={10} color="#94A2B3" />
              </Flex>
              <Stack gap={3} style={{ maxWidth: '100%', minWidth: 0, width: '100%' }}>
                <Group gap={6} style={{ flexWrap: 'wrap', maxWidth: '100%', minWidth: 0 }}>
                  {roleLabel && (
                    <Text
                      size="11px"
                      c="dimmed"
                      style={{ maxWidth: '100%', overflowWrap: 'anywhere' }}
                    >
                      {roleLabel}
                    </Text>
                  )}
                  <DatasetPill
                    datasetId={binding.dataset.id}
                    datasetName={binding.dataset.name}
                    dataset={binding.dataset}
                  />
                </Group>
              </Stack>
            </Group>
          );
        })}
      </Stack>
      {isCollapsible && (
        <UnstyledButton
          aria-expanded={isExpanded}
          aria-label={toggleLabel}
          onClick={() => setIsExpanded((value) => !value)}
          style={{
            alignItems: 'center',
            alignSelf: 'flex-start',
            color: '#6B7280',
            display: 'inline-flex',
            fontSize: '12px',
            fontWeight: 500,
            gap: 2,
            lineHeight: 1.2,
            marginLeft: '26px',
          }}
        >
          {toggleLabel}
          {isExpanded ? <IconChevronUp size={13} /> : <IconChevronDown size={13} />}
        </UnstyledButton>
      )}
    </Stack>
  );
};

export default StepRunDataflowSection;
